import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Modal } from '../ui/Modal'

/**
 * Piesele predarilor folosite si in Sampling → Predari, si pe pagina activitatii (Marfa →
 * Predari & PV). Mutate aici din `SamplingPage` (15.10.2026), ca acelasi lucru sa fie un singur
 * cod: bonul, retrimiterea bonurilor respinse, etichetele starilor.
 */

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

export const EMAIL_LABEL: Record<string, string> = {
  nou: 'În curs',
  trimis: 'Trimis',
  esuat: 'Eșuat',
}

export const BON_LABEL: Record<string, string> = {
  nu_se_aplica: '—',
  nou: 'Netrimis',
  trimis: 'Trimis',
  esuat: 'Eșuat',
  oprit: 'Oprit',
  agregat: 'La închidere',
  stornat: 'Stornat',
}

interface PreviewBon {
  payload: Record<string, unknown>
  corp: string
  piedici: string[]
}

/** Ce s-ar trimite în EA pentru o predare — sau ce s-a trimis deja. */
export function ModalBon({
  predareId,
  canWrite,
  onClose,
}: {
  predareId: number
  canWrite: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const preview = useQuery({
    queryKey: ['sampling', 'bon', predareId],
    queryFn: async () =>
      (await api.get<PreviewBon>(`/marketing/sampling/predari/${predareId}/bon`)).data,
  })

  const trimite = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ trimis: boolean; docNo: string | null; motiv: string | null }>(
          `/marketing/sampling/predari/${predareId}/bon`,
        )
      ).data,
    onSuccess: (r) => {
      if (r.trimis) toast.success(`Bon emis în EA${r.docNo ? `: ${r.docNo}` : ''}`)
      else toast.error(r.motiv ?? 'Bonul nu a plecat')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  return (
    <Modal open onClose={onClose} size="lg" title="Bon de consum — ce se trimite în EA">
      {preview.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
      ) : !preview.data ? (
        <p className="text-sm text-b4l-red">{mesaj(preview.error)}</p>
      ) : (
        <div className="space-y-3">
          {preview.data.piedici.length > 0 && (
            <div className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
              <p className="font-medium">Nu pleacă acum:</p>
              <ul className="mt-1 list-inside list-disc">
                {preview.data.piedici.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          <pre className="max-h-96 overflow-auto rounded-lg bg-b4l-bg p-3 text-xs">
            {JSON.stringify(preview.data.payload, null, 2)}
          </pre>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Închide
        </Button>
        {canWrite && preview.data && preview.data.piedici.length === 0 && (
          <Button disabled={trimite.isPending} onClick={() => trimite.mutate()}>
            {trimite.isPending ? 'Se trimite…' : 'Trimite în EA'}
          </Button>
        )}
      </div>
    </Modal>
  )
}

interface RandRetrimis {
  nrPv: string
  trimis: boolean
  docNo: string | null
  motiv: string | null
}

/**
 * Retrimiterea în masă a bonurilor respinse de EA.
 *
 * Pe 29.09.2026, la „Farmacia Tei Craiova", 22 de bonuri au fost respinse cu „Stoc insuficient":
 * gestiunea agentului era goală. După transferul mărfii, fiecare trebuia retrimis din fereastra
 * lui — douăzeci și două de ferestre deschise și închise.
 *
 * Butonul cere o ACȚIUNE aleasă, nu lucrează peste tot ce e în listă: o retrimitere în masă
 * trebuie să aibă un contur pe care omul îl vede înainte să apese.
 */
export function BonuriEsuate({
  actiuneId,
  esuateInLista,
  canWrite,
}: {
  actiuneId: number | null
  esuateInLista: number
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  const [raport, setRaport] = useState<RandRetrimis[] | null>(null)

  const esuate = useQuery({
    queryKey: ['sampling', 'bonuri-esuate', actiuneId],
    enabled: actiuneId != null,
    queryFn: async () =>
      (
        await api.get<{ predari: { id: number; nrPv: string }[] }>(
          `/marketing/sampling/actiuni/${actiuneId}/bonuri-esuate`,
        )
      ).data.predari,
  })

  const retrimite = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          incercate: number
          reusite: number
          esuate: number
          auMaiRamas: boolean
          randuri: RandRetrimis[]
        }>(`/marketing/sampling/actiuni/${actiuneId}/bonuri-esuate/retrimite`)
      ).data,
    onSuccess: (r) => {
      if (r.reusite === r.incercate) toast.success(`Au plecat toate ${r.reusite} bonurile.`)
      else if (r.reusite === 0) toast.error(`Niciunul n-a plecat. Vezi motivul mai jos.`)
      else toast.warning(`${r.reusite} din ${r.incercate} au plecat. Restul, mai jos.`)
      // Doar cele rămase eșuate: cele reușite n-au ce căuta într-un raport de erori.
      setRaport(r.randuri.filter((x) => !x.trimis))
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /* Fără acțiune aleasă nu se poate lucra în masă — dar omul trebuie să afle de ce. */
  if (actiuneId == null) {
    if (esuateInLista === 0) return null
    return (
      <Card>
        <p className="p-3 text-sm text-b4l-amber-dark">
          {esuateInLista === 1
            ? 'Este un bon respins de ExpertAccounts în lista de mai jos.'
            : `Sunt ${esuateInLista} bonuri respinse de ExpertAccounts în lista de mai jos.`}{' '}
          Alege acțiunea din filtrul de sus ca să le poți retrimite pe toate odată.
        </p>
      </Card>
    )
  }

  const cate = esuate.data?.length ?? 0
  if (cate === 0 && raport === null) return null

  return (
    <Card>
      <div className="space-y-2 p-3">
        {cate > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-b4l-text">
              <strong>{cate}</strong> {cate === 1 ? 'bon respins' : 'bonuri respinse'} de
              ExpertAccounts pe acțiunea asta.
            </span>
            {canWrite && (
              <Button
                className="!px-2 !py-1 !text-xs"
                disabled={retrimite.isPending}
                onClick={() => {
                  setRaport(null)
                  retrimite.mutate()
                }}
              >
                {retrimite.isPending ? 'Se retrimit…' : `Retrimite toate (${cate})`}
              </Button>
            )}
            <span className="text-xs text-b4l-text-muted">
              Dacă marfa tot nu e în gestiune, EA le respinge la fel — întâi transferul.
            </span>
          </div>
        )}

        {raport !== null && raport.length > 0 && (
          <div className="rounded border border-b4l-border bg-b4l-bg-soft p-2">
            <p className="mb-1 text-xs font-medium">Au rămas respinse:</p>
            <ul className="space-y-1 text-xs text-b4l-text-muted">
              {raport.map((r) => (
                <li key={r.nrPv}>
                  <span className="font-mono">{r.nrPv}</span> — {curataMesajEa(r.motiv)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Mesajul de la EA, citibil.
 *
 * ERP-ul răspunde cu HTML: `ERR:13 :<b>Stoc insuficient</b><table…>`. Netratat, umple ecranul cu
 * etichete și tot omul trebuie să caute cele două cuvinte care contează.
 */
export function curataMesajEa(motiv: string | null): string {
  if (!motiv) return 'fără motiv de la EA'
  const text = motiv
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > 180 ? `${text.slice(0, 180)}…` : text
}
